import React, { useState, useMemo } from 'react';
import { ArrowUpRight, Clock, GraduationCap } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import SubscriptionReportSection from '../components/SubscriptionReportSection';
import ReportToolbar from '../components/ReportToolbar';

// ——— FEATURED ———
const featured = [];

// ——— ALL STUDY ITEMS ———
const items = [];

const CATEGORIES = ['UPSC'];

const signals = [];

const tierStyle = (t) => ({
  'MUST READ': 'bg-[#C8A84B] text-[#0D1B2A] border-[#C8A84B]',
  'RECOMMENDED': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'REFERENCE': 'bg-slate-50 text-slate-700 border-slate-200',
  'FOUNDATION': 'bg-emerald-50 text-emerald-700 border-emerald-200',
}[t] || 'bg-slate-50 text-slate-700 border-slate-200');

const statusStyle = (s) =>
  s === 'Published' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700';

// ——— FEATURED ———
const FeaturedCard = ({ item }) => (
  <a href={`#upsc-${item.id}`} className="group relative bg-white border border-slate-200 rounded-2xl p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(13,27,42,0.14)] hover:border-[#C8A84B]/50 overflow-hidden" data-testid={`upsc-featured-${item.id}`}>
    <div className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
      <div className="absolute top-3 right-3 w-6 h-px bg-[#C8A84B]" />
      <div className="absolute top-3 right-3 w-px h-6 bg-[#C8A84B]" />
    </div>
    <div className="flex items-start gap-4">
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${item.accent === 'indigo' ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-50 text-[#C8A84B]'}`}>
        {item.icon}
      </div>
      <div className="flex-1">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#C8A84B] font-semibold mb-2">{item.tag}</p>
        <h3 className="text-2xl md:text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight mb-3">{item.title}</h3>
        <p className="text-slate-600 leading-relaxed italic border-l-2 border-[#C8A84B]/40 pl-4">{item.summary}</p>
      </div>
    </div>
  </a>
);

const GridCard = ({ m }) => (
  <a href={`#upsc-${m.id}`} className="group relative bg-white border border-slate-200 rounded-2xl p-7 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(13,27,42,0.14)] hover:border-[#C8A84B]/50 overflow-hidden flex flex-col" data-testid={`upsc-card-${m.id}`}>
    <div className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
      <div className="absolute top-3 right-3 w-6 h-px bg-[#C8A84B]" />
      <div className="absolute top-3 right-3 w-px h-6 bg-[#C8A84B]" />
    </div>
    <div className="flex items-center justify-between mb-5">
      <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-mono font-bold tracking-[0.15em] uppercase rounded ${statusStyle(m.status)}`}>{m.status}</span>
      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-600">{m.icon}</div>
    </div>
    <div className="mb-3">
      <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-1">{m.category} <span className="text-slate-300 mx-1">·</span> {m.paper}</p>
      <h3 className="text-2xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight">{m.title}</h3>
    </div>
    <p className="text-[15px] text-slate-700 leading-snug font-medium italic border-l-2 border-[#C8A84B]/40 pl-4 mb-6">{m.subtitle}</p>
    <div className="mb-4">
      <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded border ${tierStyle(m.tier)}`}>{m.tier}</span>
    </div>
    <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100">
      <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400 tracking-widest uppercase"><Clock className="w-3 h-3" /> {m.duration}</span>
      <div className="flex items-center gap-1.5 text-sm font-medium text-[#C8A84B] group-hover:gap-3 transition-all duration-300">
        <span className="font-mono text-xs tracking-wider uppercase">Open</span>
        <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  </a>
);

const ListRow = ({ m }) => (
  <a href={`#upsc-${m.id}`} className="group flex items-center gap-6 bg-white border border-slate-200 rounded-xl px-6 py-5 transition-all duration-300 hover:border-[#C8A84B]/50 hover:shadow-md" data-testid={`upsc-row-${m.id}`}>
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-600">{m.icon}</div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 text-[9px] font-mono font-bold tracking-[0.15em] uppercase rounded ${statusStyle(m.status)}`}>{m.status}</span>
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded border ${tierStyle(m.tier)}`}>{m.tier}</span>
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{m.category} · {m.paper}</span>
      </div>
      <h3 className="text-lg font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight mb-1">{m.title}</h3>
      <p className="text-sm text-slate-600 leading-snug line-clamp-1">{m.subtitle}</p>
    </div>
    <div className="hidden md:flex flex-col items-end gap-1 text-right flex-shrink-0">
      <span className="text-[10px] font-mono text-slate-400 tracking-widest uppercase">{m.date}</span>
      <span className="text-[10px] font-mono text-slate-400 tracking-widest uppercase">{m.duration}</span>
    </div>
    <ArrowUpRight className="flex-shrink-0 w-5 h-5 text-[#C8A84B] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
  </a>
);

const UPSC = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadedReportCount, setUploadedReportCount] = useState(0);

  const filtered = useMemo(() => {
    return items.filter((m) => {
      const c = cat === 'All' || m.category === cat;
      const t = !q.trim() || [m.title, m.subtitle, m.category, m.paper].join(' ').toLowerCase().includes(q.toLowerCase());
      return c && t;
    });
  }, [q, cat]);

  return (
    <>
      <PageMeta
        title="Civil Services Intelligence | The B Tree"
        description="Explore governance, policy, economic and international affairs analysis designed for serious civil services preparation and deeper public understanding."
        canonical="https://www.thebtree.com/civil-services-intelligence"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="upsc-page">
      {/* HEADER */}
      <div className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="flex items-start justify-between mb-6 gap-6">
            <div>
              <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">Civil Services · Foundation & Mains</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-3">Civil Services Intelligence™</h1>
              <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
                Governance, policy, economy, international relations and issues shaping India.
              </p>
            </div>
          </div>
        </div>
      </div>

      <ReportToolbar
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search Polity, Economic Survey, Geography, Mains frameworks..."
        filterContent={CATEGORIES.map((c) => (
          <button key={c} type="button" onClick={() => setCat(c)} className={`h-10 rounded-lg border px-3 text-sm font-medium transition-colors ${cat === c ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-[#C8A84B]'}`}>
            {c}
          </button>
        ))}
        reportCount={uploadedReportCount}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        testId="upsc"
      />

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="mb-6">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] mb-2 font-semibold">Editor's Picks</p>
            <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A]">Featured Intelligence</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((f) => <FeaturedCard key={f.id} item={f} />)}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <SubscriptionReportSection
          category="UPSC"
          title="Uploaded Civil Services Intelligence™ Reports"
          onCountChange={setUploadedReportCount}
          viewMode={viewMode}
          filters={{
            query: q,
            primary: cat,
            secondary: '',
            primaryAllValues: ['All'],
            secondaryAllValues: [],
          }}
        />

        {items.length > 0 && (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-3'} data-testid="upsc-results">
            {filtered.map((m) => viewMode === 'grid' ? <GridCard key={m.id} m={m} /> : <ListRow key={m.id} m={m} />)}
          </div>
        )}
      </section>

      {signals.length > 0 && (
        <section className="bg-[#F5F1E8] border-y border-[#C8A84B]/20 py-16" data-testid="upsc-signals">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] mb-3 font-semibold text-center">Preparation Compass</p>
            <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] text-center mb-10">Three Rules Toppers Never Break</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {signals.map((s) => (
                <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#C8A84B]/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="font-[Cormorant_Garamond] font-medium text-4xl text-[#C8A84B] leading-none">#{s.n}</span>
                    <p className="text-[#0D1B2A] leading-relaxed font-medium">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C8A84B]/10 text-[#C8A84B] mb-6">
          <GraduationCap className="w-6 h-6" />
        </div>
        <h2 className="text-3xl md:text-5xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-4">Study like you mean to top the list</h2>
        <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">Every report, every PYQ mapping, every Mains answer framework — structured for serious aspirants.</p>
        <button
          className="inline-flex items-center gap-3 bg-[#0D1B2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#1A2B3F] transition-all duration-300 hover:gap-4 active:scale-[0.98]"
          data-testid="upsc-unlock-button"
          onClick={() => window.location.assign('/signup')}
        >
          Start Preparing <ArrowUpRight className="w-4 h-4" />
        </button>
      </section>
    </div>
    </>
  );
};

export default UPSC;
